import { isEmpty, keys, omit } from 'ramda';
import type { Editor } from 'slate';
import { Transforms } from 'slate';

import type { HeadersElement, SuggestedHeadersEdit } from '../internal';
import { AlineTransforms, EditingMode, Queries } from '../internal';

type HeadersElementProps = Omit<HeadersElement, 'children' | 'suggestedEdits' | 'type'>;

const HeaderTransforms = {
  setHeadersElementProps(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props:
        | Partial<HeadersElementProps>
        | ((oldProps: Omit<HeadersElement, 'children'>) => Partial<HeadersElementProps>);
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, props } = options;
      const headersEntry = Queries.getHeadersElement(editor);
      if (!headersEntry) return;

      const [headers, headersPath] = headersEntry;
      const newProps = typeof props === 'function' ? props(omit(['children'], headers)) : props;
      const equalKeys = keys(newProps).filter(propKey => {
        switch (propKey) {
          case 'headerMargin': {
            const activeHeaderMargin = Queries.renderedHeaderOrFooterMargin(headers, 'in');
            return activeHeaderMargin === newProps.headerMargin;
          }
        }
      });

      const nonEqualProps = omit(equalKeys, newProps);
      if (isEmpty(nonEqualProps)) return;

      if (editingMode === EditingMode.SUGGESTING) {
        const headersEdit: SuggestedHeadersEdit = {
          authorId,
          type: 'headerFormat',
          value: nonEqualProps,
        };

        AlineTransforms.suggestHeadersEdit(editor, { editingMode, headersEdit });

        return;
      }

      AlineTransforms.removeConflictingSuggestions(editor, {
        editingMode,
        entry: headersEntry,
        props: nonEqualProps,
      });
      Transforms.setNodes(editor, newProps, { at: headersPath });
    });
  },
};

export default HeaderTransforms;
